// src/components/ModalsContainer/ModalsContainer.js
import React from "react";
import PropTypes from "prop-types";
import { useBulkOperationsStore } from "../../stores";

// Modal Components
import EditDisplayNameModal from "./EditDisplayNameModal";
import EditCredentialsSettingModal from "./EditCredentialsSettingModal";
import WebSocketReconnectModal from "../../WebSocketReconnectModal";
import ForcedEntryAlertModal from "../../ForcedEntryAlertModal";
import ProppedDoorAlertModal from "../../ProppedDoorAlertModal";
import EventDeniedModal from "../../EventDeniedModal";
import AdvancedSettingsModal from "../../AdvancedSettingsModal";
import DeviceClaimingModal from "../../DeviceClaimingModal";
import DeleteCredentialConfirmModal from "../CredentialManagement/modals/DeleteCredentialConfirmModal";
import InstallationActionsModal from "../../InstallationActionsModal";
import InstallationManagementModal from "../../InstallationManagementModal";
import OrganizationManagementModal from "../../OrganizationManagementModal";
import { BulkActionStatusModal } from "../BulkOperations";
import SessionExpirationWarning from "../SessionExpirationWarning";
import PasswordPromptModal from "../CredentialManagement/modals/PasswordPromptModal";
import DeviceUpdateBanner from "./DeviceUpdateBanner";

/**
 * Centralized container for all modal dialogs and overlays
 * Manages rendering of 15+ modals with their state and callbacks
 *
 * This component takes all modal state and props from AppContent and
 * renders the appropriate modals. This extraction reduces AppContent.js
 * by ~550 lines and improves code organization.
 */
const ModalsContainer = (props) => {
  // Get bulkDeviceLockdownStates from bulkOperationsStore (Phase 1 Days 43-45)
  const bulkDeviceLockdownStates = useBulkOperationsStore(
    (state) => state.bulkDeviceLockdownStates,
  );

  const {
    // Edit Display Name Modal
    // Edit Display Name Modal
    isEditingDisplayName,
    editedDisplayName,
    currentDisplayName,
    isSavingDisplayName,
    onDisplayNameChange,
    onDisplayNameKeyDown,
    onCancelDisplayNameEdit,
    onSaveDisplayName,

    // WebSocket Reconnect Modal
    showWsReconnectModal,
    connectionStatus,
    onReconnect,

    // Alert Modals
    forcedEntryDevice,
    onCloseForcedEntryAlert,
    proppedDoorDevice,
    onCloseProppedDoorAlert,
    denyEventData,
    onCloseDenyEventModal,

    // Advanced Settings Modal
    showAdvancedSettingsModal,
    orgId,
    organizationName,
    userRole,
    onSettingsUpdated,
    onCloseAdvancedSettingsModal,

    // Device Claiming Modal
    showDeviceClaimingModal,
    devices,
    onDevicesClaimed,
    onCloseDeviceClaimingModal,

    // Delete Credential Confirm Modal
    showDeleteConfirmModal,
    credentialId,
    patronName,
    deviceName,
    isDeleting,
    onConfirmDeleteCredential,
    onCancelDeleteCredential,

    // Installation Actions Modal
    showInstallActionsModal,
    deviceCount,
    eligibleForLockdown,
    eligibleForRelease,
    eligibleDevicesForLockdown,
    eligibleDevicesForRelease,
    installationName,
    allDeviceStatesLoaded,
    onExecuteAction,
    onRetrySync,
    onCloseInstallActionsModal,
    onManageInstallation,

    // Installation Management Modal
    showInstallManagementModal,
    theme,
    installations,
    currentInstallationId,
    organizationId,
    userProfile,
    getAccessToken,
    onInstallationsChanged,
    onSwitchInstallation,
    onChangeInstallation,
    devicesForInstallManagement,
    onCloseInstallManagementModal,

    // Organization Management Modal
    showOrgManagementModal,
    organizations,
    currentOrganizationId,
    onOrganizationsChanged,
    onSwitchOrganization,
    onChangeOrganization,
    onCloseOrgManagementModal,
    onOpenInstallationManagement,

    // Bulk Action Status Modal
    showBulkStatusModal,
    bulkActionResults,
    bulkActionStatuses,
    bulkActionTimestamps,
    deviceStates,
    bulkActionType,
    bulkInstallationName,
    // bulkDeviceLockdownStates now from bulkOperationsStore
    onCloseBulkStatusModal,

    // Overlays
    configError,
    isLoading,
    loadingMessage,
    notification,
    onCloseNotification,

    // Password Prompt Modal
    showPasswordModal,
    passwordModalTitle,
    passwordModalMessage,
    onClosePasswordModal,
    onPasswordConfirm,

    // Credentials Setting Modal
    credentialsSettingModal,

    // Device Update Modal (firmware/WiFi change notification)
    authenticatedFetch,
    installationId,
  } = props;

  return (
    <>
      {/* Edit Display Name Modal */}
      <EditDisplayNameModal
        isOpen={isEditingDisplayName}
        value={editedDisplayName}
        currentName={currentDisplayName}
        isSaving={isSavingDisplayName}
        onChange={onDisplayNameChange}
        onKeyDown={onDisplayNameKeyDown}
        onCancel={onCancelDisplayNameEdit}
        onSave={onSaveDisplayName}
      />

      {/* Edit Credentials Setting Modal */}
      {credentialsSettingModal && (
        <EditCredentialsSettingModal
          isOpen={credentialsSettingModal.isOpen}
          settingKey={credentialsSettingModal.settingKey}
          settingLabel={credentialsSettingModal.settingLabel}
          currentValue={credentialsSettingModal.currentValue}
          settingConfig={credentialsSettingModal.settingConfig}
          isSaving={credentialsSettingModal.isSaving}
          onSave={credentialsSettingModal.onSave}
          onCancel={credentialsSettingModal.onCancel}
        />
      )}

      {/* WebSocket Reconnect Modal */}
      {showWsReconnectModal && (
        <WebSocketReconnectModal
          connectionStatus={connectionStatus}
          onReconnect={onReconnect}
        />
      )}

      {/* Forced Entry Alert Modal */}
      <ForcedEntryAlertModal
        isOpen={!!forcedEntryDevice}
        deviceId={forcedEntryDevice?.deviceId}
        deviceName={forcedEntryDevice?.displayName}
        timestamp={forcedEntryDevice?.timestamp}
        onClose={onCloseForcedEntryAlert}
      />

      {/* Propped Door Alert Modal */}
      <ProppedDoorAlertModal
        isOpen={!!proppedDoorDevice}
        deviceId={proppedDoorDevice?.deviceId}
        deviceName={proppedDoorDevice?.displayName}
        timestamp={proppedDoorDevice?.timestamp}
        onClose={onCloseProppedDoorAlert}
      />

      {/* Event Denied Modal */}
      <EventDeniedModal
        isOpen={!!denyEventData}
        deviceId={denyEventData?.deviceId}
        deviceName={denyEventData?.displayName}
        timestamp={denyEventData?.timestamp}
        actionType={denyEventData?.actionType}
        actionDetails={denyEventData?.actionDetails}
        onClose={onCloseDenyEventModal}
      />

      {/* Advanced Settings Modal */}
      <AdvancedSettingsModal
        isOpen={showAdvancedSettingsModal}
        orgId={orgId}
        organizationName={organizationName}
        userRole={userRole}
        onSettingsUpdated={onSettingsUpdated}
        onClose={onCloseAdvancedSettingsModal}
      />

      {/* Device Claiming Modal */}
      <DeviceClaimingModal
        isOpen={showDeviceClaimingModal}
        devices={devices}
        onDevicesClaimed={onDevicesClaimed}
        onClose={onCloseDeviceClaimingModal}
      />

      {/* Delete Credential Confirm Modal */}
      <DeleteCredentialConfirmModal
        isOpen={showDeleteConfirmModal}
        credentialId={credentialId}
        patronName={patronName}
        deviceName={deviceName}
        isDeleting={isDeleting}
        onConfirm={onConfirmDeleteCredential}
        onClose={onCancelDeleteCredential}
      />

      {/* Installation Actions Modal */}
      <InstallationActionsModal
        isOpen={showInstallActionsModal}
        deviceCount={deviceCount}
        eligibleForLockdown={eligibleForLockdown}
        eligibleForRelease={eligibleForRelease}
        eligibleDevicesForLockdown={eligibleDevicesForLockdown}
        eligibleDevicesForRelease={eligibleDevicesForRelease}
        installationName={installationName}
        allDeviceStatesLoaded={allDeviceStatesLoaded}
        onExecuteAction={onExecuteAction}
        onRetrySync={onRetrySync}
        onClose={onCloseInstallActionsModal}
        onManageInstallation={onManageInstallation}
      />

      {/* Installation Management Modal */}
      <InstallationManagementModal
        isOpen={showInstallManagementModal}
        theme={theme}
        installations={installations}
        currentInstallationId={currentInstallationId}
        organizationId={organizationId}
        userProfile={userProfile}
        getAccessToken={getAccessToken}
        onInstallationsChanged={onInstallationsChanged}
        onSwitchInstallation={onSwitchInstallation}
        onChangeInstallation={onChangeInstallation}
        devices={devicesForInstallManagement}
        onClose={onCloseInstallManagementModal}
      />

      {/* Organization Management Modal */}
      <OrganizationManagementModal
        isOpen={showOrgManagementModal}
        theme={theme}
        organizations={organizations}
        currentOrganizationId={currentOrganizationId}
        userProfile={userProfile}
        getAccessToken={getAccessToken}
        onOrganizationsChanged={onOrganizationsChanged}
        onSwitchOrganization={onSwitchOrganization}
        onChangeOrganization={onChangeOrganization}
        onOpenInstallationManagement={onOpenInstallationManagement}
        onClose={onCloseOrgManagementModal}
      />

      {/* Bulk Action Status Modal */}
      <BulkActionStatusModal
        isOpen={showBulkStatusModal}
        actionResults={bulkActionResults}
        actionStatuses={bulkActionStatuses}
        actionTimestamps={bulkActionTimestamps}
        deviceStates={deviceStates}
        actionType={bulkActionType}
        installationName={bulkInstallationName}
        deviceLockStates={bulkDeviceLockdownStates}
        onClose={onCloseBulkStatusModal}
      />

      {/* Full Screen Overlays */}
      {configError && (
        <div className="error-overlay">
          <div className="error-message-box">
            <h2>Configuration Error</h2>
            <p>{configError}</p>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{loadingMessage || "Loading..."}</p>
        </div>
      )}

      {/* Notification Popup */}
      {notification && (
        <div
          className={`notification-popup notification-${
            notification.type || "info"
          }`}
        >
          {notification.message || notification}
          {onCloseNotification && (
            <button
              onClick={onCloseNotification}
              className="close-notification"
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Session Expiration Warning */}
      <SessionExpirationWarning />

      {/* Password Prompt Modal */}
      <PasswordPromptModal
        isOpen={showPasswordModal}
        title={passwordModalTitle}
        message={passwordModalMessage}
        onClose={onClosePasswordModal}
        onConfirm={onPasswordConfirm}
      />

      {/* Device Update Banner (firmware/WiFi change notification) */}
      <DeviceUpdateBanner
        authenticatedFetch={authenticatedFetch}
        installationId={installationId}
      />
    </>
  );
};

ModalsContainer.propTypes = {
  // Edit Display Name Modal
  isEditingDisplayName: PropTypes.bool,
  editedDisplayName: PropTypes.string,
  currentDisplayName: PropTypes.string,
  isSavingDisplayName: PropTypes.bool,
  onDisplayNameChange: PropTypes.func,
  onDisplayNameKeyDown: PropTypes.func,
  onCancelDisplayNameEdit: PropTypes.func,
  onSaveDisplayName: PropTypes.func,

  // WebSocket Reconnect Modal
  showWsReconnectModal: PropTypes.bool,
  connectionStatus: PropTypes.string,
  onReconnect: PropTypes.func,

  // Alert Modals
  forcedEntryDevice: PropTypes.object,
  onCloseForcedEntryAlert: PropTypes.func,
  proppedDoorDevice: PropTypes.object,
  onCloseProppedDoorAlert: PropTypes.func,
  denyEventData: PropTypes.object,
  onCloseDenyEventModal: PropTypes.func,

  // Advanced Settings Modal
  showAdvancedSettingsModal: PropTypes.bool,
  orgId: PropTypes.string,
  organizationName: PropTypes.string,
  userRole: PropTypes.object,
  onSettingsUpdated: PropTypes.func,
  onCloseAdvancedSettingsModal: PropTypes.func,

  // Device Claiming Modal
  showDeviceClaimingModal: PropTypes.bool,
  devices: PropTypes.array,
  onDevicesClaimed: PropTypes.func,
  onCloseDeviceClaimingModal: PropTypes.func,

  // Delete Credential Confirm Modal
  showDeleteConfirmModal: PropTypes.bool,
  credentialId: PropTypes.string,
  patronName: PropTypes.string,
  deviceName: PropTypes.string,
  isDeleting: PropTypes.bool,
  onConfirmDeleteCredential: PropTypes.func,
  onCancelDeleteCredential: PropTypes.func,

  // Installation Actions Modal
  showInstallActionsModal: PropTypes.bool,
  deviceCount: PropTypes.number,
  eligibleForLockdown: PropTypes.number,
  eligibleForRelease: PropTypes.number,
  eligibleDevicesForLockdown: PropTypes.array,
  eligibleDevicesForRelease: PropTypes.array,
  installationName: PropTypes.string,
  allDeviceStatesLoaded: PropTypes.bool,
  onExecuteAction: PropTypes.func,
  onRetrySync: PropTypes.func,
  onCloseInstallActionsModal: PropTypes.func,
  onManageInstallation: PropTypes.func,

  // Installation Management Modal
  showInstallManagementModal: PropTypes.bool,
  theme: PropTypes.string,
  installations: PropTypes.array,
  currentInstallationId: PropTypes.string,
  organizationId: PropTypes.string,
  userProfile: PropTypes.object,
  getAccessToken: PropTypes.func,
  onInstallationsChanged: PropTypes.func,
  onSwitchInstallation: PropTypes.func,
  onChangeInstallation: PropTypes.func,
  devicesForInstallManagement: PropTypes.array,
  onCloseInstallManagementModal: PropTypes.func,

  // Organization Management Modal
  showOrgManagementModal: PropTypes.bool,
  organizations: PropTypes.array,
  currentOrganizationId: PropTypes.string,
  onOrganizationsChanged: PropTypes.func,
  onSwitchOrganization: PropTypes.func,
  onChangeOrganization: PropTypes.func,
  onCloseOrgManagementModal: PropTypes.func,
  onOpenInstallationManagement: PropTypes.func,

  // Bulk Action Status Modal
  showBulkStatusModal: PropTypes.bool,
  bulkActionResults: PropTypes.array,
  bulkActionStatuses: PropTypes.object,
  deviceStates: PropTypes.object,
  bulkActionType: PropTypes.string,
  bulkInstallationName: PropTypes.string,
  // bulkDeviceLockdownStates now from bulkOperationsStore
  onCloseBulkStatusModal: PropTypes.func,

  // Overlays
  configError: PropTypes.string,
  isLoading: PropTypes.bool,
  loadingMessage: PropTypes.string,
  notification: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onCloseNotification: PropTypes.func,

  // Password Prompt Modal
  showPasswordModal: PropTypes.bool,
  passwordModalTitle: PropTypes.string,
  passwordModalMessage: PropTypes.string,
  onClosePasswordModal: PropTypes.func,
  onPasswordConfirm: PropTypes.func,

  // Credentials Setting Modal
  credentialsSettingModal: PropTypes.shape({
    isOpen: PropTypes.bool,
    settingKey: PropTypes.string,
    settingLabel: PropTypes.string,
    currentValue: PropTypes.string,
    isSaving: PropTypes.bool,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
  }),

  // Device Update Modal (firmware/WiFi change notification)
  authenticatedFetch: PropTypes.func,
  installationId: PropTypes.string,
};

export default ModalsContainer;
